import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios'

import { API_VERSION, env } from '@/lib/env'
import { toApiError } from '@/lib/problem-details'

import { session } from './session'
import type { AuthSession } from './types'

const baseURL = `${env.apiBaseUrl}/${API_VERSION}`

interface RetryableConfig extends InternalAxiosRequestConfig {
  /** Set once a request has already been replayed after a token refresh. Guarantees one retry. */
  retriedAfterRefresh?: boolean
}

/**
 * Endpoints that must never trigger the refresh-and-retry path. A 401 from any of them is the
 * answer, not a symptom: refreshing in response would loop.
 */
const AUTH_PATHS = ['/auth/login', '/auth/refresh', '/auth/mfa', '/auth/password/change', '/auth/logout']

const isAuthPath = (url: string | undefined) => Boolean(url && AUTH_PATHS.some((path) => url.startsWith(path)))

export const apiClient = axios.create({
  baseURL,
  // Required for the HttpOnly refresh cookie to ride along. In development the Vite proxy keeps
  // the API same-origin so a SameSite=Strict cookie is still sent.
  withCredentials: true,
  timeout: 30_000,
  headers: {
    Accept: 'application/json, application/problem+json',
  },
})

/**
 * A bare client for the refresh call itself. It deliberately shares none of the interceptors
 * below, so a failing refresh cannot re-enter the refresh path.
 */
const refreshClient = axios.create({ baseURL, withCredentials: true, timeout: 15_000 })

const newCorrelationId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`

apiClient.interceptors.request.use((config) => {
  const token = session.getAccessToken()
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`)
  }

  const branchId = session.getBranchId()
  if (branchId) {
    config.headers.set('X-Branch-Id', branchId)
  }

  // Matches the correlation id on the server's request log, audit entries, and any journal the
  // request posts — the thread a support call is traced along.
  config.headers.set('X-Correlation-Id', newCorrelationId())

  return config
})

let refreshInFlight: Promise<string> | null = null

/**
 * Exchanges the HttpOnly refresh cookie for a fresh access token. Concurrent callers share one
 * in-flight request: a dashboard firing six queries at once must not rotate the refresh token six
 * times, which the server would read as token reuse and answer by revoking the whole family.
 */
async function refreshAccessToken(): Promise<string> {
  refreshInFlight ??= refreshClient
    .post<AuthSession>('/auth/refresh')
    .then((response) => {
      session.setAccessToken(response.data.accessToken)
      return response.data.accessToken
    })
    .finally(() => {
      refreshInFlight = null
    })

  return refreshInFlight
}

apiClient.interceptors.response.use(undefined, async (error: AxiosError) => {
  const config = error.config as RetryableConfig | undefined

  const isExpiredToken =
    error.response?.status === 401 && config && !config.retriedAfterRefresh && !isAuthPath(config.url)

  if (!isExpiredToken) {
    throw error
  }

  try {
    const token = await refreshAccessToken()
    config.retriedAfterRefresh = true
    config.headers.set('Authorization', `Bearer ${token}`)
    return await apiClient.request(config)
  } catch {
    // The refresh cookie is missing, expired, or was rejected as reused. There is no second
    // attempt: the session is over and the shell moves the user to sign-in.
    session.notifyExpired()
    throw error
  }
})

// Runs after the refresh interceptor, so anything reaching a caller is already a settled failure.
// Every consumer — queries, mutations, error surfaces — therefore sees one normalised shape and
// never has to know Axios exists.
apiClient.interceptors.response.use(undefined, (error: unknown) => {
  throw toApiError(error)
})

let restoreInFlight: Promise<AuthSession | null> | null = null

/**
 * Restores a session from the refresh cookie at start-up, when the in-memory token is empty.
 *
 * The promise is cached for the life of the page rather than per call. Refresh tokens rotate and
 * the server treats a second use of a spent token as theft, so start-up must produce exactly one
 * refresh — including under React's development double-invocation of effects.
 */
export function restoreSession(): Promise<AuthSession | null> {
  restoreInFlight ??= refreshClient
    .post<AuthSession>('/auth/refresh')
    .then((response) => {
      session.setAccessToken(response.data.accessToken)
      return response.data
    })
    .catch(() => null)

  return restoreInFlight
}
